/**
 * DimensionVerificationWorkflow Component
 * 
 * DigiPART-native workflow for dimension verification and extraction.
 * No file uploads - queries parts directly from DigiPART API.
 * 
 * Workflow Steps:
 * 1. Connection Check - Verify DigiPART authentication
 * 2. Part Selection - Enter catalogue ID and select parts from DigiPART
 * 3. Configuration - Model selection,processing options
 * 4. Review & Start - Confirm and launch verification job
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import {
  Database,
  Search,
  Settings,
  Play,
  Home,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Box,
  FileText,
  DollarSign,
  Clock,
  Zap,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { authAPI, dimensionsAPI, jobsAPI, globalPromptsAPI } from '../../services/api';
import type { DigiPARTPart, DigiPARTConnectionStatus, DigiPARTCatalogue } from '../../types';
import ModelSelect from '../../components/ModelSelect';

interface PipelineConfig {
  default_model: string;
  default_chunk_size: number;
  default_temperature: number;
  default_max_concurrent: number;
}

interface CostEstimate {
  estimated_cost: {
    usd: number;
    sgd: number;
  };
  estimated_tokens: {
    total_input_tokens: number;
    estimated_output_tokens: number;
    grand_total_tokens: number;
  };
  breakdown: {
    parts_total: number;
    parts_counted: number;
    avg_tokens_per_part: number;
    model_name: string;
    input_rate_per_million: number;
    output_rate_per_million: number;
    output_token_multiplier: number;
    token_method: 'sdk' | 'sdk_with_fallback';
    is_estimate: boolean;
  };
  processing_time_minutes: number;
}

const DimensionVerificationWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Stable per-attempt idempotency key — reset only after a confirmed new job.
  const idempotencyKeyRef = React.useRef<string>(crypto.randomUUID());
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  
  // Step 1: Connection state
  const [connectionStatus, setConnectionStatus] = useState<DigiPARTConnectionStatus | null>(null);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [connectionError, setConnectionError] = useState('');
  
  // Step 2: Part selection state
  const [catalogues, setCatalogues] = useState<DigiPARTCatalogue[]>([]);
  const [loadingCatalogues, setLoadingCatalogues] = useState(false);
  const [catalogueLoadError, setCatalogueLoadError] = useState(false);
  const [selectedCatalogueId, setSelectedCatalogueId] = useState<string>('');
  const [parts, setParts] = useState<DigiPARTPart[]>([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [partsError, setPartsError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [shapeFilter, setShapeFilter] = useState<string>('all');
  const [inScopeOnly, setInScopeOnly] = useState<boolean>(true);
  const [maxParts, setMaxParts] = useState<number | null>(null); // Limit for testing (null = unlimited)
  const [maxPartsError, setMaxPartsError] = useState<string | null>(null);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(false);
  const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(new Set());
  
  // Progress tracking for loading all parts
  const [loadingProgress, setLoadingProgress] = useState(0); // Current count of loaded parts
  const [totalPartsEstimate, setTotalPartsEstimate] = useState(0); // Total expected parts
  
  // Step 3: Configuration state
  const [pipelineConfig, setPipelineConfig] = useState<PipelineConfig | null>(null);
  const [modelName, setModelName] = useState('gemini-2.5-flash');
  const [temperature, setTemperature] = useState(0.1);
  
  // Step 4: Cost & submission state
  const [estimatedCost, setEstimatedCost] = useState<CostEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [starting, setStarting] = useState(false);
  
  // Load connection status on mount
  useEffect(() => {
    checkConnection();
    fetchPipelineConfig();
  }, []);
  
  // Re-check connection when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnection();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkConnection);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkConnection);
    };
  }, []);
  
  // Load catalogues when step 2 is reached
  useEffect(() => {
    if (currentStep === 2 && catalogues.length === 0 && !loadingCatalogues && connectionStatus?.authenticated) {
      loadCatalogues();
    }
  }, [currentStep, connectionStatus]);
  
  // Auto-load parts when catalogue is selected
  useEffect(() => {
    if (currentStep === 2 && selectedCatalogueId && parts.length === 0 && !loadingParts) {
      loadAllParts();
    }
  }, [currentStep, selectedCatalogueId]);
  
  const checkConnection = async () => {
    setLoadingConnection(true);
    setConnectionError('');
    try {
      const response = await authAPI.digipartSessionStatus();
      setConnectionStatus(response.data as any);
      
      if (!response.data.authenticated) {
        if (!response.data.assigned) {
          setConnectionError('No DigiPART account assigned. Contact your administrator.');
        } else {
          setConnectionError('Not connected to DigiPART. Click the button below to open the DigiPART login.');
        }
      }
    } catch (error) {
      setConnectionError('Failed to check DigiPART connection');
    } finally {
      setLoadingConnection(false);
    }
  };
  
  const fetchPipelineConfig = async () => {
    try {
      setPipelineConfig({
        default_model: 'gemini-2.5-flash',
        default_chunk_size: 5,
        default_temperature: 0.1,
        default_max_concurrent: 3,
      });
    } catch (error) {
      console.error('Failed to fetch pipeline config:', error);
    }
  };
  
  const loadCatalogues = async () => {
    setLoadingCatalogues(true);
    setCatalogueLoadError(false);
    try {
      const response = await dimensionsAPI.getCatalogues();
      setCatalogues(response.data.catalogues || []);
    } catch (error: any) {
      const httpStatus = error?.response?.status;
      if (httpStatus === 401 || httpStatus === 403) {
        // Session expired while user was on the page — surface the auth banner
        setConnectionStatus(null);
        setConnectionError('Your DigiPART session has expired. Please reconnect and try again.');
      } else {
        setCatalogueLoadError(true);
      }
    } finally {
      setLoadingCatalogues(false);
    }
  };
  
  const loadParts = async () => {
    if (!selectedCatalogueId) {
      setPartsError('Please select a project');
      return;
    }
    
    setLoadingParts(true);
    setPartsError('');
    try {
      // Use maxParts if set, otherwise default to 500
      const pageSize = maxParts || 500;
      const filters: any = {
        catalogue_id: selectedCatalogueId,
        first: pageSize,
      };
      
      if (searchQuery) filters.part_number = searchQuery;
      if (shapeFilter && shapeFilter !== 'all') filters.shape = shapeFilter;
      
      if (inScopeOnly) {
        filters.in_scope = true;
      }
      
      const response = await dimensionsAPI.queryParts(filters);
      setParts(response.data.parts || []);
      
      // Log pagination info
      const limitMsg = maxParts ? ` (limited to ${maxParts})` : '';
      console.log(`Loaded ${response.data.parts?.length || 0} parts${limitMsg}`);
      console.log(`Total in catalogue: ${response.data.total_count || 0}`);
      console.log(`After filtering: ${response.data.filtered_count || 0}`);
      
    } catch (error: any) {
      setPartsError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to load parts from DigiPART'
      );
    } finally {
      setLoadingParts(false);
    }
  };
  
  const loadAllParts = async () => {
    if (!selectedCatalogueId) {
      setPartsError('Please select a project');
      return;
    }
    
    setLoadingParts(true);
    setPartsError('');
    setLoadingProgress(0);
    setTotalPartsEstimate(0);
    
    try {
      let allParts: any[] = [];
      let after: string | null = null;
      let pageCount = 0;
      const pageSize = 1000; // Fetch 1000 parts per page
      
      do {
        pageCount++;
        
        // Stop if we've reached the max parts limit
        if (maxParts && allParts.length >= maxParts) {
          console.log(`✅ Reached max parts limit: ${maxParts}`);
          break;
        }
        const filters: any = {
          catalogue_id: selectedCatalogueId,
          first: pageSize,
        };
        
        if (after) filters.after = after;
        if (searchQuery) filters.part_number = searchQuery;
        if (shapeFilter && shapeFilter !== 'all') filters.shape = shapeFilter;
        if (inScopeOnly) filters.in_scope = true;
        
        const response = await dimensionsAPI.queryParts(filters);
        let newParts = response.data.parts || [];
        
        // Trim if we exceed max parts limit
        if (maxParts && allParts.length + newParts.length > maxParts) {
          const remaining = maxParts - allParts.length;
          newParts = newParts.slice(0, remaining);
          console.log(`Trimming last page to ${remaining} parts to respect limit of ${maxParts}`);
        }
        
        allParts = [...allParts, ...newParts];
        
        // Update progress tracking
        if (pageCount === 1 && response.data.total_count) {
          // Set estimate from first page response (or maxParts if limit is set)
          const totalAvailable = response.data.total_count;
          const effectiveTotal = maxParts && maxParts < totalAvailable ? maxParts : totalAvailable;
          setTotalPartsEstimate(effectiveTotal);
        }
        setLoadingProgress(allParts.length);
        
        // Update UI with progress
        setParts(allParts);
        console.log(`Page ${pageCount}: Loaded ${newParts.length} parts (Total so far: ${allParts.length}${totalPartsEstimate > 0 ? ` / ~${totalPartsEstimate}` : ''})`);
        
        // Check if there's more data
        const pageInfo = response.data.page_info;
        if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
          after = pageInfo.endCursor;
        } else {
          after = null; // No more pages
        }
        
        // Also stop if we've reached the limit
        if (maxParts && allParts.length >= maxParts) {
          after = null;
        }
        
      } while (after !== null);
      
      const limitMessage = maxParts ? ` (limited to ${maxParts})` : '';
      console.log(`✅ Loaded parts: ${allParts.length} total (from ${pageCount} pages)${limitMessage}`);
      
    } catch (error: any) {
      setPartsError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to load all parts from DigiPART'
      );
      console.error('Load all parts error:', error);
    } finally {
      setLoadingParts(false);
      setLoadingProgress(0);
      setTotalPartsEstimate(0);
    }
  };
  
  const togglePartSelection = (partId: string) => {
    const newSelection = new Set(selectedPartIds);
    if (newSelection.has(partId)) {
      newSelection.delete(partId);
    } else {
      newSelection.add(partId);
    }
    setSelectedPartIds(newSelection);
  };
  
  const selectAllParts = () => {
    setSelectedPartIds(new Set(parts.map(p => p.id)));
  };
  
  const clearSelection = () => {
    setSelectedPartIds(new Set());
  };
  
  const estimateCost = async () => {
    if (selectedPartIds.size === 0) return;

    setEstimating(true);
    setEstimateError(null);
    setEstimatedCost(null);
    try {
      // Build part objects from the already-loaded parts state — no DigiPART re-fetch needed
      const selectedParts = parts
        .filter(p => selectedPartIds.has(p.id))
        .map(p => ({ id: p.id, part_number: p.part_number, shape: p.shape }));

      const response = await dimensionsAPI.estimateCost({
        parts: selectedParts,
        model_name: modelName,
        output_token_multiplier: 1.5,
      });
      setEstimatedCost(response.data);
    } catch (error: any) {
      console.error('Failed to estimate cost:', error);
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        'Cost estimation failed. Please try again.';
      setEstimateError(msg);
    } finally {
      setEstimating(false);
    }
  };
  
  const handleStartProcessing = async () => {
    if (selectedPartIds.size === 0) return;
    
    setStarting(true);
    try {
      const response = await jobsAPI.start({
        file_id: '', // Not used for DigiPART workflow
        pipeline_type: 'dimensions_verification',
        model_name: modelName,
        temperature: temperature,
        config: {
          part_ids: Array.from(selectedPartIds),
          catalogue_id: selectedCatalogueId,
        },
      }, idempotencyKeyRef.current);
      idempotencyKeyRef.current = crypto.randomUUID();
      if (response.data.is_replay) {
        toast({ title: 'Job already submitted', description: 'This job was already submitted — showing your existing job.', variant: 'warning', duration: 5000 });
      }
      navigate('/jobs');
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Failed to start processing job';
      toast({ title: 'Job Start Failed', description: msg, variant: 'error' });
    } finally {
      setStarting(false);
    }
  };
  
  const canProceedToStep = (step: number): boolean => {
    switch (step) {
      case 2:
        return (connectionStatus as any)?.authenticated === true;
      case 3:
        return selectedCatalogueId !== '' && selectedPartIds.size > 0;
      case 4:
        return selectedCatalogueId !== '' && selectedPartIds.size > 0;
      default:
        return true;
    }
  };
  
  // Step renderers
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className={`${theme.card.background} rounded-lg shadow p-6`}>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-4 flex items-center`}>
          <Database className="w-6 h-6 mr-2" />
          DigiPART Connection Check
        </h2>
        
        {loadingConnection ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-interactive-primary" />
            <span className={`ml-3 ${theme.text.secondary}`}>Checking connection...</span>
          </div>
        ) : connectionStatus && (connectionStatus as any).authenticated ? (
          <div className="space-y-4">
            <div className="p-4 bg-qc-success/10 rounded-lg border border-qc-success/30">
              <div className="flex items-center">
                <CheckCircle className="w-6 h-6 text-qc-success mr-3" />
                <div className="flex-1">
                  <p className="font-semibold text-qc-success">
                    Connected to DigiPART
                  </p>
                  <p className={`text-sm ${theme.text.secondary} mt-1`}>
                    Logged in as: {(connectionStatus as any).user_email}
                  </p>
                  {(connectionStatus as any).organization_name && (
                    <p className={`text-sm ${theme.text.secondary}`}>
                      Organization: {(connectionStatus as any).organization_name}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {(connectionStatus as any).connection_tested_at && (
              <p className={`text-sm ${theme.text.secondary}`}>
                Last tested: {new Date((connectionStatus as any).connection_tested_at).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-qc-warning/10 border border-qc-warning/30">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-qc-warning mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-qc-warning">
                    Action Required
                  </p>
                  <p className={`text-sm ${theme.text.secondary} mt-1`}>
                    {!(connectionStatus as any)?.assigned
                      ? 'No DigiPART account assigned. Contact your administrator.'
                      : 'Your DigiPART session has expired or is not active. Open the DigiPART login tab to reconnect.'}
                  </p>
                  <Link
                    to="/profile?tab=digipart"
                    className="mt-3 inline-block px-4 py-2 bg-qc-warning hover:bg-qc-warning/90 text-text-inverse rounded-lg transition"
                  >
                    Login to DigiPART
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end">
        <button
          onClick={() => setCurrentStep(2)}
          disabled={!canProceedToStep(2)}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition flex items-center"
        >
          Next: Select Parts
          <ChevronRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </div>
  );
  
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className={`${theme.card.background} rounded-lg shadow p-6`}>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-4 flex items-center`}>
          <Search className="w-6 h-6 mr-2" />
          Select Parts for Verification
        </h2>
        
        {/* Connected Organization Display */}
        {connectionStatus?.organization_name && (
          <div className="mb-6 p-4 bg-interactive-primary/10 border border-interactive-primary/30 rounded-lg">
            <div className="flex items-center">
              <Database className="w-5 h-5 mr-2 text-interactive-primary" />
              <div>
                <p className="text-sm font-medium text-interactive-primary">
                  Connected Organization
                </p>
                <p className="text-lg font-semibold text-interactive-primary">
                  {connectionStatus.organization_name}
                </p>
                <p className="text-xs text-interactive-primary mt-1">
                  Logged in as: {connectionStatus.user_email}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Project/Catalogue Selection */}
        <div className="mb-6">
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Select DigiPART Project <span className="text-text-tertiary" aria-hidden="true">*</span>
          </label>
          {loadingCatalogues ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-interactive-primary" />
              <span className={`ml-3 ${theme.text.secondary}`}>Loading projects...</span>
            </div>
          ) : catalogues.length > 0 ? (
            <select
              value={selectedCatalogueId}
              onChange={(e) => {
                setSelectedCatalogueId(e.target.value);
                setParts([]);
                setSelectedPartIds(new Set());
              }}
              data-testid="catalogue-select"
              className={`w-full px-4 py-2 border ${selectedCatalogueId ? 'border-qc-success' : 'border-border'} rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-2 focus:ring-interactive-primary focus:border-transparent`}
            >
              <option value="">-- Select a Project --</option>
              {catalogues.map((catalogue) => (
                <option key={catalogue.id} value={catalogue.id}>
                  {catalogue.name} ({catalogue.spare_parts_count || 0} parts)
                </option>
              ))}
            </select>
          ) : (
            <div
              data-testid="catalogue-load-error"
              className="flex items-center justify-between px-4 py-3 border border-qc-error/30 rounded-lg bg-qc-error/10"
            >
              <span className="text-sm text-qc-error">
                {catalogueLoadError
                  ? 'Could not load projects from DigiPART. Check your connection and try again.'
                  : 'No projects found in DigiPART.'}
              </span>
              <button
                type="button"
                onClick={loadCatalogues}
                className="ml-4 text-sm font-medium text-interactive-primary hover:underline"
              >
                Retry
              </button>
            </div>
          )}
          <p className={`text-xs ${theme.text.secondary} mt-1`}>
            Select the DigiPART project/catalogue you want to process parts from
          </p>
        </div>
        
        {/* In Scope Filter */}
        <div className="mb-6 p-4 bg-bg-subtle rounded-lg border border-border">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={inScopeOnly}
              onChange={(e) => {
                setInScopeOnly(e.target.checked);
                if (parts.length > 0) {
                  if (showAdvancedSearch && (searchQuery || shapeFilter !== 'all')) {
                    loadParts();
                  } else {
                    loadAllParts();
                  }
                }
              }}
              className="rounded mr-3 w-5 h-5 text-interactive-primary"
            />
            <div className="flex-1">
              <p className={`font-semibold ${theme.text.primary}`}>
                In-Scope Parts Only
              </p>
              <p className={`text-sm ${theme.text.secondary}`}>
                Only show and process parts marked as "in scope" to avoid overprocessing
              </p>
            </div>
          </label>
        </div>
        
        {/* Advanced Search - Collapsible */}
        <div className="mb-6">
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className="w-full flex items-center justify-between p-4 bg-bg-subtle rounded-lg border border-border hover:bg-bg-subtle transition"
          >
            <div className="flex items-center">
              <Search className="w-5 h-5 mr-2 text-text-secondary" />
              <span className={`font-semibold ${theme.text.primary}`}>
                Advanced Search & Filters
              </span>
              <span className={`ml-2 text-sm ${theme.text.secondary}`}>
                (Optional - Proof of Concept)
              </span>
            </div>
            <ChevronRight className={`w-5 h-5 transform transition-transform ${showAdvancedSearch ? 'rotate-90' : ''}`} />
          </button>
          
          {showAdvancedSearch && (
            <div className="mt-4 p-4 border border-border rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Search by Part Number
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && loadParts()}
                    placeholder="Enter part number..."
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-interactive-primary focus:border-transparent`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Filter by Shape
                  </label>
                  <select
                    value={shapeFilter}
                    onChange={(e) => setShapeFilter(e.target.value)}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-interactive-primary focus:border-transparent`}
                  >
                    <option value="all">All Shapes</option>
                    <option value="TUBE">TUBE</option>
                    <option value="CUBOID">CUBOID</option>
                    <option value="DN">DN</option>
                    <option value="UNKNOWN">UNKNOWN</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Max Parts Limit (Optional)
                  </label>
                  <input
                    type="number"
                    value={maxParts ?? ''}
                    onChange={(e) => {
                      if (e.target.value === '') {
                        setMaxPartsError(null);
                        setMaxParts(null);
                        return;
                      }
                      const num = e.target.valueAsNumber;
                      if (Number.isInteger(num) && num >= 1) {
                        setMaxPartsError(null);
                        setMaxParts(Math.min(num, 100_000));
                      } else {
                        setMaxPartsError('Enter a whole number of 1 or more.');
                      }
                    }}
                    placeholder="Unlimited"
                    min="1"
                    max="100000"
                    step="1"
                    data-testid="max-parts-input"
                    aria-invalid={maxPartsError != null}
                    aria-describedby="max-parts-hint"
                    className={`w-full px-4 py-2 border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:border-transparent ${
                      maxPartsError
                        ? 'border-qc-error focus:ring-qc-error'
                        : 'border-border focus:ring-interactive-primary'
                    }`}
                  />
                  {maxPartsError
                    ? <p id="max-parts-hint" className="mt-1 text-xs text-qc-error" role="alert">{maxPartsError}</p>
                    : <p id="max-parts-hint" className="mt-1 text-xs text-text-tertiary">Leave empty to process all parts. Put a number to filter.</p>
                  }
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={loadParts}
                  disabled={loadingParts}
                  className="flex-1 px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary text-text-inverse font-semibold rounded-lg transition flex items-center justify-center"
                >
                  {loadingParts && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                  {loadingParts ? 'Searching...' : 'Apply Filters'}
                </button>
                <button
                  onClick={loadAllParts}
                  disabled={loadingParts}
                  className="flex-1 px-6 py-3 bg-qc-success hover:bg-qc-success/90 disabled:bg-bg-subtle disabled:text-text-tertiary text-text-inverse font-semibold rounded-lg transition flex items-center justify-center"
                  title="Load ALL parts from DigiPART using pagination (may take time for large catalogues)"
                >
                  {loadingParts && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                  {loadingParts ? 'Loading All...' : 'Load All Parts'}
                </button>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setShapeFilter('all');
                    setMaxParts(null);
                    loadParts();
                  }}
                  className="px-6 py-3 bg-bg-hover hover:bg-bg-hover text-text-secondary font-semibold rounded-lg transition"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Loading Progress Bar */}
        {loadingParts && totalPartsEstimate > 0 && (
          <div className="mb-4 p-4 rounded-lg bg-interactive-primary/10 border border-interactive-primary/30">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-interactive-primary">
                  Loading Parts...
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold text-interactive-primary">
                  {loadingProgress.toLocaleString()} / {totalPartsEstimate.toLocaleString()} 
                  {' '}({((loadingProgress / totalPartsEstimate) * 100).toFixed(1)}%)
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-3 text-xs flex rounded bg-interactive-primary/10">
              <div 
                style={{ width: `${Math.min((loadingProgress / totalPartsEstimate) * 100, 100)}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-text-inverse justify-center bg-interactive-primary transition-all duration-300"
              />
            </div>
          </div>
        )}
        
        {partsError && (
          <div className="mb-4 p-4 rounded-lg bg-qc-error/10 border border-qc-error/30">
            <p className="text-qc-error">{partsError}</p>
          </div>
        )}
        
        {/* Selection Summary */}
        {parts.length > 0 && (
          <div className="mb-4 flex items-center justify-between p-4 bg-bg-subtle rounded-lg">
            <div className="flex items-center gap-3">
              <Box className="w-5 h-5 text-interactive-primary shrink-0" />
              <div>
                <span className={`font-semibold ${theme.text.primary}`}>
                  {selectedPartIds.size.toLocaleString()} of {parts.length.toLocaleString()} parts selected
                </span>
                {totalPartsEstimate > 0 && totalPartsEstimate !== parts.length && (
                  <p className={`text-xs ${theme.text.secondary} mt-0.5`}>
                    {(totalPartsEstimate - parts.length).toLocaleString()} parts excluded by active filters
                    {inScopeOnly ? ' (in-scope filter)' : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAllParts}
                className="px-3 py-1 text-sm bg-interactive-primary/10 text-interactive-primary rounded hover:bg-interactive-primary/20 transition"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                className="px-3 py-1 text-sm bg-bg-hover text-text-secondary rounded hover:bg-bg-hover transition"
              >
                Clear
              </button>
            </div>
          </div>
        )}
        
        {/* Parts Table */}
        {parts.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-bg-subtle">
                  <tr>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text.secondary}`}>
                      <input
                        type="checkbox"
                        checked={selectedPartIds.size === parts.length && parts.length > 0}
                        onChange={() => selectedPartIds.size === parts.length ? clearSelection() : selectAllParts()}
                        className="rounded"
                      />
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text.secondary}`}>
                      Part Number
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text.secondary}`}>
                      Shape
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text.secondary}`}>
                      Existing Dims
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${theme.text.secondary}`}>
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {parts.map((part) => (
                    <tr
                      key={part.id}
                      onClick={() => togglePartSelection(part.id)}
                      className={`cursor-pointer hover:bg-bg-subtle transition ${
                        selectedPartIds.has(part.id) ? 'bg-interactive-primary/10' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedPartIds.has(part.id)}
                          onChange={() => {}}
                          className="rounded"
                        />
                      </td>
                      <td className={`px-4 py-3 font-mono text-sm ${theme.text.primary}`}>
                        {part.part_number}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          part.shape === 'TUBE' ? 'bg-interactive-primary/10 text-interactive-primary' :
                          part.shape === 'CUBOID' ? 'bg-qc-success/10 text-qc-success' :
                          part.shape === 'DN' ? 'bg-bg-subtle text-text-secondary' :
                          'bg-bg-subtle text-text-secondary'
                        }`}>
                          {part.shape}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-sm ${theme.text.secondary}`}>
                        {part.existing_dimensions_count}
                      </td>
                      <td className={`px-4 py-3 text-sm ${theme.text.secondary} truncate max-w-xs`}>
                        {part.description || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {parts.length === 0 && !loadingParts && !partsError && selectedCatalogueId && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-text-tertiary mb-4" />
            <p className={`${theme.text.secondary}`}>No parts found in this catalogue</p>
          </div>
        )}
      </div>
      
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(1)}
          className="px-6 py-3 bg-bg-hover hover:bg-bg-hover text-text-secondary font-semibold rounded-lg transition"
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentStep(3)}
          disabled={!canProceedToStep(3)}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition flex items-center"
        >
          Next: Configure
          <ChevronRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </div>
  );
  
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className={`${theme.card.background} rounded-lg shadow p-6`}>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
          <Settings className="w-6 h-6 mr-2" />
          Processing Configuration
        </h2>
        
        <div className="space-y-6">
          <div>
            <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
              AI Model
            </label>
            <ModelSelect value={modelName} onChange={setModelName} preferredDefault="gemini-2.5-flash" />
          </div>
          
          <div>
            <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
              Temperature: {temperature}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>
      
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(2)}
          className="px-6 py-3 bg-bg-hover hover:bg-bg-hover text-text-secondary font-semibold rounded-lg transition"
        >
          Previous
        </button>
        <button
          onClick={() => {
            estimateCost();
            setCurrentStep(4);
          }}
          disabled={!canProceedToStep(4)}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed text-text-inverse font-semibold rounded-lg transition flex items-center"
        >
          Next: Review
          <ChevronRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </div>
  );
  
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className={`${theme.card.background} rounded-lg shadow p-6`}>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
          <Play className="w-6 h-6 mr-2" />
          Review & Start
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="p-4 bg-bg-subtle rounded-lg">
            <p className={`text-sm ${theme.text.secondary} mb-1`}>Parts to Process</p>
            <p className={`text-2xl font-bold ${theme.text.primary}`}>{selectedPartIds.size}</p>
          </div>
          
          <div className="p-4 bg-bg-subtle rounded-lg">
            <p className={`text-sm ${theme.text.secondary} mb-1`}>Estimated Cost</p>
            {estimatedCost ? (
              <div>
                <p className={`text-2xl font-bold ${theme.text.primary}`}>
                  ${estimatedCost.estimated_cost.usd.toFixed(4)}
                </p>
                <p className={`text-xs ${theme.text.secondary} mt-1`}>
                  SGD ${estimatedCost.estimated_cost.sgd.toFixed(4)}
                </p>
              </div>
            ) : estimating ? (
              <Loader2 className="w-6 h-6 animate-spin text-interactive-primary" />
            ) : (
              <span className={`text-sm ${theme.text.secondary}`}>&mdash;</span>
            )}
          </div>
          
          <div className="p-4 bg-bg-subtle rounded-lg">
            <p className={`text-sm ${theme.text.secondary} mb-1`}>Processing Time</p>
            {estimatedCost ? (
              <p className={`text-2xl font-bold ${theme.text.primary}`}>
                {estimatedCost.processing_time_minutes} min
              </p>
            ) : estimating ? (
              <Loader2 className="w-6 h-6 animate-spin text-interactive-primary" />
            ) : (
              <span className={`text-sm ${theme.text.secondary}`}>&mdash;</span>
            )}
          </div>
        </div>

        {/* Cost estimation error */}
        {estimateError && (
          <div className="mb-4 p-4 bg-qc-error/10 border border-qc-error/30 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-qc-error mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-qc-error">Cost Estimation Failed</p>
              <p className="text-sm text-qc-error mt-0.5">{estimateError}</p>
              <button
                onClick={estimateCost}
                disabled={estimating}
                className="mt-2 text-sm font-medium text-qc-error underline hover:no-underline disabled:opacity-50"
              >
                {estimating ? 'Retrying…' : 'Retry estimate'}
              </button>
            </div>
          </div>
        )}

        {/* Token Breakdown */}
        {estimatedCost && (
          <div className="mb-6 p-4 bg-interactive-primary/10 border border-interactive-primary/30 rounded-lg">
            <h3 className={`text-sm font-semibold ${theme.text.primary} mb-3 flex items-center`}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Token Usage Breakdown
              {estimatedCost.breakdown.token_method === 'sdk' ? (
                <span className="ml-2 px-2 py-0.5 text-xs bg-qc-success/10 text-qc-success rounded-full font-medium">
                  Exact — Google SDK
                </span>
              ) : (
                <span className="ml-2 px-2 py-0.5 text-xs bg-qc-warning/10 text-qc-warning rounded-full font-medium">
                  ~approx. ±30%
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex justify-between items-center py-2 px-3 bg-bg-surface rounded">
                <span className={`text-sm ${theme.text.secondary}`}>Input Tokens:</span>
                <span className={`font-mono font-semibold ${theme.text.primary}`}>
                  {estimatedCost.estimated_tokens.total_input_tokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 bg-bg-surface rounded">
                <span className={`text-sm ${theme.text.secondary}`}>Output Tokens (est.):</span>
                <span className={`font-mono font-semibold ${theme.text.primary}`}>
                  {estimatedCost.estimated_tokens.estimated_output_tokens.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 bg-bg-surface rounded">
                <span className={`text-sm ${theme.text.secondary}`}>Total Tokens:</span>
                <span className={`font-mono font-semibold text-interactive-primary`}>
                  {estimatedCost.estimated_tokens.grand_total_tokens.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-interactive-primary/30">
              <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
                <span className={theme.text.secondary}>
                  Model: <span className="font-mono">{estimatedCost.breakdown.model_name}</span>
                </span>
                <span className={theme.text.secondary}>
                  Rates: <span className={`font-mono font-semibold ${theme.text.primary}`}>
                    ${estimatedCost.breakdown.input_rate_per_million}/M in
                  </span>
                  {' · '}
                  <span className={`font-mono font-semibold ${theme.text.primary}`}>
                    ${estimatedCost.breakdown.output_rate_per_million}/M out
                  </span>
                </span>
                <span className={theme.text.secondary}>
                  All <span className={`font-semibold ${theme.text.primary}`}>{estimatedCost.breakdown.parts_counted}</span> parts counted
                  {' — '}
                  avg <span className={`font-mono font-semibold ${theme.text.primary}`}>
                    {estimatedCost.breakdown.avg_tokens_per_part.toLocaleString()}
                  </span> tokens/part
                </span>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-3 mb-6">
          <div className="flex justify-between py-2 border-b border-border">
            <span className={theme.text.secondary}>Model:</span>
            <span className={`ml-2 ${theme.text.primary} font-mono`}>{modelName}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-border">
            <span className={theme.text.secondary}>Temperature:</span>
            <span className={`ml-2 ${theme.text.primary}`}>{temperature}</span>
          </div>
        </div>
        
        <div className="mb-6 p-4 bg-qc-warning/10 border border-qc-warning/30 rounded-lg">
          <p className={`text-sm ${theme.text.secondary}`}>
            This will validate existing dimensions and extract missing dimensions from documents for {selectedPartIds.size} parts.
            You will be charged approximately ${estimatedCost?.estimated_cost.usd.toFixed(4)} USD.
          </p>
        </div>
        
        <button
          onClick={handleStartProcessing}
          disabled={starting}
          className="w-full bg-qc-success hover:bg-qc-success/90 disabled:bg-bg-subtle disabled:text-text-tertiary text-text-inverse font-bold py-4 px-6 rounded-lg transition flex items-center justify-center"
        >
          {starting && <Loader2 className="w-6 h-6 mr-2 animate-spin" />}
          {starting ? 'Starting Processing...' : 'Start Verification Job'}
        </button>
      </div>
      
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(3)}
          className="px-6 py-3 bg-bg-hover hover:bg-bg-hover text-text-secondary font-semibold rounded-lg transition"
        >
          Previous
        </button>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-bg-base">
      {/* Breadcrumb */}
      <div className="border-b border-border bg-bg-surface/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-text-tertiary hover:text-text-primary transition">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4 text-text-tertiary" />
            <Link to="/select-pipeline" className="text-text-tertiary hover:text-text-primary transition">
              Pipelines
            </Link>
            <ChevronRight className="w-4 h-4 text-text-tertiary" />
            <span className="text-text-primary font-medium" aria-current="page">Dimension Verification</span>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-text-primary mb-2">
            DigiPART Dimension Verification
          </h2>
          <p className="text-text-secondary">
            Verify and extract dimensions directly from DigiPART parts
          </p>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Connection' },
              { num: 2, label: 'Select Parts' },
              { num: 3, label: 'Configure' },
              { num: 4, label: 'Review' },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center w-full">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep >= step.num
                        ? 'bg-interactive-primary text-text-inverse'
                        : 'bg-bg-hover text-text-tertiary'
                    }`}
                  >
                    {step.num}
                  </div>
                  <p className={`mt-2 text-sm ${currentStep >= step.num ? 'text-text-primary font-medium' : 'text-text-tertiary'}`}>
                    {step.label}
                  </p>
                </div>
                {idx < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step.num ? 'bg-interactive-primary' : 'bg-bg-hover'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Step Content */}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>
    </div>
  );
};

export default DimensionVerificationWorkflow;
